import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Board from '../components/Board';
import { useTitle } from '../components/Layout';
import { useProgress } from '../progress';
import { fromFen, applyMove, movesFrom, isWhite, isCheckmate, withPromotion, toSan, PIECE_NAMES } from '../chess';
import { scenarios } from '../data';
import { puzzleNumber } from './Puzzles';

export default function PuzzleRoute() {
  const { puzzleId } = useParams();
  const scenario = scenarios.find((s) => s.id === Number(puzzleId));
  if (!scenario) return <Navigate to="/puzzles" replace />;
  return <Puzzle key={scenario.id} scenario={scenario} />;
}

function Puzzle({ scenario }) {
  useTitle(`Puzzle ${puzzleNumber(scenario)}`);
  const { completePuzzle } = useProgress();
  const start = useMemo(() => fromFen(scenario.fen), [scenario.fen]);

  // Every visit starts from a fresh board so the puzzle can be played again.
  // Rating and the "Solved" badge are remembered separately in progress.
  const [position, setPosition] = useState(start);
  const [lastMove, setLastMove] = useState(null);
  const [status, setStatus] = useState('idle');
  const [played, setPlayed] = useState(null);
  const [selected, setSelected] = useState(null);
  const [wrongKey, setWrongKey] = useState(0);
  const resultRef = useRef(null);

  const finished = status === 'solved' || status === 'revealed';
  const unlocked = finished;
  const targets = selected ? movesFrom(position, selected) : [];

  const index = scenarios.findIndex((s) => s.id === scenario.id);
  const prev = scenarios[index - 1];
  const next = scenarios[index + 1];

  useEffect(() => {
    if (status === 'wrong') {
      const t = setTimeout(() => setStatus('idle'), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, wrongKey]);

  const onSquareClick = (sq) => {
    if (finished) return;
    const piece = position[sq];
    if (piece && isWhite(piece)) {
      setSelected(sq === selected ? null : sq);
      return;
    }
    if (!selected || !targets.includes(sq)) return;

    const move = withPromotion(position, { from: selected, to: sq });
    const after = applyMove(position, move);
    setSelected(null);
    if (isCheckmate(after, false)) {
      setPosition(after);
      setLastMove(move);
      setPlayed(`${toSan(position, move)}#`);
      setStatus('solved');
      completePuzzle(scenario.id, 'solved');
    } else {
      setStatus('wrong');
      setWrongKey((k) => k + 1);
    }
  };

  const reveal = () => {
    setPosition(applyMove(start, scenario.solution));
    setLastMove(scenario.solution);
    setPlayed(scenario.san);
    setSelected(null);
    setStatus('revealed');
    completePuzzle(scenario.id, 'revealed');
    requestAnimationFrame(() => resultRef.current && resultRef.current.focus({ preventScroll: true }));
  };

  const replay = () => {
    setPosition(start);
    setLastMove(null);
    setPlayed(null);
    setSelected(null);
    setStatus('idle');
  };

  let message;
  if (status === 'solved') message = <>Checkmate. <span className="mono">{played}</span> — well played.</>;
  else if (status === 'revealed') message = <>The winning move: <span className="mono">{played}</span></>;
  else if (status === 'wrong') message = <>Not mate. Black escapes. Try another move.</>;
  else if (selected) message = <>{PIECE_NAMES[position[selected].toLowerCase()]} selected. Choose a square.</>;
  else message = <>White to move. Find the checkmate in one.</>;

  return (
    <div className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/puzzles">← All puzzles</Link>
        <span className="muted mono">
          {puzzleNumber(scenario)} / {String(scenarios.length).padStart(2, '0')}
        </span>
      </nav>

      <header className="puzzle-header">
        <p className="eyebrow">
          {[scenario.company, scenario.location].filter(Boolean).join(' · ')}
        </p>
        <h1 className="page-title">{scenario.title}</h1>
        <ul className="chips">
          <li className="chip chip-accent">{scenario.theme}</li>
          {scenario.tags.map((t) => (
            <li key={t} className="chip">
              {t}
            </li>
          ))}
        </ul>
      </header>

      <div className="puzzle-layout">
        <aside className="puzzle-board">
          <div key={wrongKey} className={status === 'wrong' ? 'shake' : ''}>
            <Board
              position={position}
              lastMove={lastMove}
              selected={selected}
              targets={targets}
              isClickable={(sq) => !finished && ((position[sq] && isWhite(position[sq])) || targets.includes(sq))}
              onSquareClick={onSquareClick}
              ariaLabel="Puzzle board. White to move."
            />
          </div>
          <div className={`eval ${finished ? 'is-won' : ''}`} aria-hidden="true">
            <span className="eval-fill" />
            <span className="eval-label mono">{finished ? '1–0' : '0.0'}</span>
          </div>
          <p className={`puzzle-status is-${status}`} role="status">
            {message}
          </p>
          <div className="puzzle-actions">
            {finished ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={replay}>
                Replay position
              </button>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm" onClick={reveal}>
                Reveal the move
              </button>
            )}
          </div>
        </aside>

        <div className="puzzle-content">
          <section className="pz-section">
            <h2 className="pz-heading">The position</h2>
            <p>{scenario.context}</p>
          </section>

          <section className="pz-section">
            <h2 className="pz-heading">Threats on the board</h2>
            <ul className="bullets">
              {scenario.challenge.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </section>

          {unlocked ? (
            <div ref={resultRef} tabIndex={-1} className="unlocked">
              <section className="pz-section">
                <h2 className="pz-heading">
                  The move <span className="muted">· my approach</span>
                </h2>
                <p className="move-note">
                  <span className="mono">{scenario.san}</span> {scenario.note}
                </p>
                <ul className="bullets">
                  {scenario.approach.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </section>

              <section className="pz-section">
                <h2 className="pz-heading">
                  Evaluation <span className="muted">· the impact</span>
                </h2>
                <div className="headline">
                  <span className="headline-value">{scenario.headline.value}</span>
                  <span className="headline-label">{scenario.headline.label}</span>
                </div>
                <ul className="bullets">
                  {scenario.impact.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
                {scenario.links && (
                  <div className="link-row">
                    {scenario.links.map((l) => (
                      <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                        {l.title} ↗
                      </a>
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : (
            <section className="card locked">
              <span className="lock-glyph" aria-hidden="true">
                ♛
              </span>
              <div>
                <h2 className="card-heading">My approach and the impact are locked</h2>
                <p>Find the checkmate on the board to unlock them, or skip straight to the answer.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={reveal}>
                  Reveal the move
                </button>
              </div>
            </section>
          )}
        </div>
      </div>

      <nav className="next-move" aria-label="More puzzles">
        {prev ? (
          <Link to={`/puzzles/${prev.id}`} className="next-move-link">
            <span className="eyebrow">← Puzzle {puzzleNumber(prev)}</span>
            <span className="next-move-title">{prev.title}</span>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`/puzzles/${next.id}`} className="next-move-link is-next">
            <span className="eyebrow">Puzzle {puzzleNumber(next)} →</span>
            <span className="next-move-title">{next.title}</span>
          </Link>
        ) : (
          <Link to="/endgame" className="next-move-link is-next">
            <span className="eyebrow">Next move →</span>
            <span className="next-move-title">Endgame</span>
          </Link>
        )}
      </nav>
    </div>
  );
}
